import {
    createTradeLogicDraft,
    flattenLogicNodes,
    buildDefaultNativeScheduleParameters,
    normalizeImportedRawConditionNodes,
    type LogicActionNode,
    type LogicConditionNode,
    type LogicGroupNode,
    type LogicNode,
    type LogicNodeBase,
    type LogicReferenceNode,
    type LogicScheduleNode,
    type LogicSequenceNode,
    type TradeLogicContextFeatureRef,
    type TradeLogicDiagnostic,
    type TradeLogicDraft,
    type TradeLogicFeatureFilter,
    type TradeLogicJsonObject,
    type TradeLogicJsonValue,
    type TradeLogicTrailingMode
} from '../model';
import type { BarDuration, TradeLogicComparisonOperatorV2 } from '@/types/appRouter';
import type { EntityTypeUnion } from '@einhaus/project314-types';
import type {
    SerializedContextFeatureRef,
    SerializedFeatureFilter,
    SerializedLogicNode,
    SerializedLogicNodeBase,
    TradeLogicAuthoringOperationPayloads,
    TradeLogicAuthoringPreviewPayload,
    TradeLogicDraftBackendPayload
} from './types';

const FEATURE_REF_VALIDATION_OPERATOR_VALUES = [
    '=',
    '>',
    '>=',
    '<',
    '<=',
    '!=',
    'any',
    'all',
    'contains',
    'not_contains',
    'empty',
    'not_empty'
] as const;

type FeatureRefValidationOperator = (typeof FEATURE_REF_VALIDATION_OPERATOR_VALUES)[number];

export interface ContextFeatureRefValidationPayload {
    kind: 'contextFeature';
    featureKey: string;
    duration: BarDuration | null;
    bindingKey: string | null;
    entityType: EntityTypeUnion['entityType'] | null;
    entityKey: string | null;
    selectors: TradeLogicJsonObject | null;
    filters: { field: string; operator: FeatureRefValidationOperator; value: TradeLogicJsonValue | null }[] | null;
    offsetBars: number | null;
    lookbackBars: number | null;
    trailingMode: TradeLogicTrailingMode | null;
    withinBars: number | null;
    missingDataPolicy: TradeLogicContextFeatureRef['missingDataPolicy'] | null;
}

const FEATURE_REF_VALIDATION_OPERATORS = new Set<FeatureRefValidationOperator>(FEATURE_REF_VALIDATION_OPERATOR_VALUES);

const isFeatureRefValidationOperator = (operator: TradeLogicComparisonOperatorV2): operator is FeatureRefValidationOperator =>
    FEATURE_REF_VALIDATION_OPERATORS.has(operator as FeatureRefValidationOperator);

const normalizeScheduleParametersForBackend = (node: LogicScheduleNode): TradeLogicJsonObject => {
    if (node.scheduleKind === 'custom' && Object.keys(node.parameters).length === 0) {
        return buildDefaultNativeScheduleParameters();
    }

    return node.parameters;
};

const FEATURE_REF_VALIDATION_DURATIONS = new Set<BarDuration>([
    '1m',
    '3m',
    '5m',
    '15m',
    '30m',
    '60m',
    '120m',
    '180m',
    '240m',
    '1d',
    '1w',
    '1mo'
]);

const isBarDuration = (value: string): value is BarDuration => FEATURE_REF_VALIDATION_DURATIONS.has(value as BarDuration);

const isEntityType = (value: string): value is EntityTypeUnion['entityType'] =>
    value === 'Asset' || value === 'Cryptocurrency' || value === 'AssetIndex';

interface SerializeTradeLogicDraftParams {
    draft: TradeLogicDraft;
    strategyId: number | null;
    registryManifestFingerprint?: string | null;
}

interface BuildTradeLogicAuthoringPreviewPayloadParams extends SerializeTradeLogicDraftParams {
    diagnostics: TradeLogicDiagnostic[];
    entryDraft?: TradeLogicDraft;
    exitDraft?: TradeLogicDraft;
}

export const serializeTradeLogicDraftForBackend = ({
    draft,
    registryManifestFingerprint,
    strategyId
}: SerializeTradeLogicDraftParams): TradeLogicDraftBackendPayload => {
    const normalizedRootNode = normalizeImportedRawConditionNodes(draft.rootNode);

    return {
        clientDraftId: draft.draftId,
        strategyId,
        kind: draft.kind,
        schemaVersion: draft.schemaVersion,
        registryManifestFingerprint: registryManifestFingerprint ?? draft.registryManifestFingerprint ?? null,
        label: draft.name,
        description: draft.description ?? null,
        scope: draft.scope,
        status: draft.status,
        sourceDefinitionId: draft.sourceDefinitionId ?? null,
        sourceVersion: draft.sourceVersion ?? null,
        evaluationModes: draft.evaluationModes,
        ...(draft.kind === 'exit' && draft.priceFillModel === 'resting_level' ? { priceFillModel: 'resting_level' as const } : {}),
        rootNode: normalizedRootNode ? serializeLogicNode(normalizedRootNode) : null,
        metadata: draft.metadata ?? null
    };
};

export const hydrateTradeLogicDraftFromBackend = (payload: TradeLogicDraftBackendPayload): TradeLogicDraft => {
    const draft = createTradeLogicDraft({
        draftId: payload.clientDraftId,
        name: payload.label,
        description: payload.description,
        kind: payload.kind,
        scope: payload.scope,
        schemaVersion: payload.schemaVersion,
        rootNode: normalizeImportedRawConditionNodes(payload.rootNode ? hydrateLogicNodeFromBackend(payload.rootNode) : null),
        evaluationModes: payload.evaluationModes,
        priceFillModel: payload.kind === 'exit' && payload.priceFillModel === 'resting_level' ? 'resting_level' : null,
        registryManifestFingerprint: payload.registryManifestFingerprint,
        metadata: payload.metadata ?? undefined
    });

    return {
        ...draft,
        sourceDefinitionId: payload.sourceDefinitionId,
        sourceVersion: payload.sourceVersion,
        status: payload.status
    };
};

export const serializeContextFeatureRefForValidation = (
    featureRef: TradeLogicContextFeatureRef | null
): ContextFeatureRefValidationPayload | null => {
    if (!featureRef) return null;

    const duration = featureRef.duration && isBarDuration(featureRef.duration) ? featureRef.duration : null;
    const entityType = featureRef.entityType && isEntityType(featureRef.entityType) ? featureRef.entityType : null;

    const filters =
        featureRef.filters?.flatMap((filter) =>
            isFeatureRefValidationOperator(filter.operator)
                ? [
                      {
                          field: filter.field,
                          operator: filter.operator,
                          value: filter.value ?? null
                      }
                  ]
                : []
        ) ?? null;

    return {
        kind: 'contextFeature',
        featureKey: featureRef.featureKey,
        duration,
        bindingKey: featureRef.bindingKey ?? null,
        entityType,
        entityKey: featureRef.entityKey ?? null,
        selectors: featureRef.selectors ?? null,
        filters,
        offsetBars: featureRef.offsetBars ?? null,
        lookbackBars: featureRef.lookbackBars ?? null,
        trailingMode: featureRef.trailingMode ?? null,
        withinBars: featureRef.withinBars ?? null,
        missingDataPolicy: featureRef.missingDataPolicy ?? null
    };
};

export const buildTradeLogicAuthoringPreviewPayload = ({
    diagnostics,
    draft,
    entryDraft,
    exitDraft,
    registryManifestFingerprint,
    strategyId
}: BuildTradeLogicAuthoringPreviewPayloadParams): TradeLogicAuthoringPreviewPayload => {
    const serializedPrimaryDraft = serializeTradeLogicDraftForBackend({
        draft,
        registryManifestFingerprint,
        strategyId
    });

    const serializedEntryDraft = entryDraft
        ? serializeTradeLogicDraftForBackend({
              draft: entryDraft,
              registryManifestFingerprint,
              strategyId
          })
        : draft.kind === 'entry'
          ? serializedPrimaryDraft
          : null;

    const serializedExitDraft = exitDraft
        ? serializeTradeLogicDraftForBackend({
              draft: exitDraft,
              registryManifestFingerprint,
              strategyId
          })
        : draft.kind === 'exit'
          ? serializedPrimaryDraft
          : null;

    const entryNodeCount = entryDraft?.rootNode
        ? flattenLogicNodes(entryDraft.rootNode).length
        : draft.kind === 'entry' && draft.rootNode
          ? flattenLogicNodes(draft.rootNode).length
          : 0;

    const exitNodeCount = exitDraft?.rootNode
        ? flattenLogicNodes(exitDraft.rootNode).length
        : draft.kind === 'exit' && draft.rootNode
          ? flattenLogicNodes(draft.rootNode).length
          : 0;

    const primaryNodeCount = draft.rootNode ? flattenLogicNodes(draft.rootNode).length : 0;

    return {
        generatedAt: new Date().toISOString(),
        draft: serializedPrimaryDraft,
        drafts: {
            entry: serializedEntryDraft,
            exit: serializedExitDraft
        },
        localDiagnostics: diagnostics,
        metadata: {
            nodeCount: primaryNodeCount,
            entryNodeCount,
            exitNodeCount,
            totalNodeCount: entryNodeCount + exitNodeCount,
            errorCount: diagnostics.filter((diagnostic) => diagnostic.severity === 'error').length,
            warningCount: diagnostics.filter((diagnostic) => diagnostic.severity === 'warning').length
        }
    };
};

export const buildTradeLogicAuthoringOperationPayloads = (
    previewPayload: TradeLogicAuthoringPreviewPayload
): TradeLogicAuthoringOperationPayloads => {
    return {
        validateDraft: {
            strategyId: previewPayload.draft.strategyId,
            draft: previewPayload.draft
        },
        compileDraft: {
            strategyId: previewPayload.draft.strategyId,
            draft: previewPayload.draft
        },
        saveDraft: {
            strategyId: previewPayload.draft.strategyId,
            draft: previewPayload.draft
        },
        publishDraft: {
            strategyId: previewPayload.draft.strategyId,
            draft: previewPayload.draft,
            localDiagnostics: previewPayload.localDiagnostics
        },
        searchGlobalCandidates: {
            status: 'active',
            limit: 100
        },
        getStrategyCandidateAttachments: null,
        previewCandidate: null,
        previewBaseCandidate:
            previewPayload.draft.strategyId === null
                ? null
                : {
                      strategyId: previewPayload.draft.strategyId
                  },
        materializeBaseCandidate:
            previewPayload.draft.strategyId === null
                ? null
                : {
                      strategyId: previewPayload.draft.strategyId,
                      replaceExistingBaseAttachment: true
                  },
        createCandidate: null,
        getCandidateCoverage: null,
        getBestCandidateBacktest: null,
        queueCandidateBacktests: null,
        queueAeroJob: null,
        importAeroResult: null,
        promoteCandidate: null
    };
};

const serializeLogicNode = (node: LogicNode): SerializedLogicNode => {
    const baseNode = createBaseNodePayload(node);

    switch (node.kind) {
        case 'group':
            return {
                ...baseNode,
                kind: 'group',
                operator: node.operator,
                threshold: node.threshold ?? null,
                weightThreshold: node.weightThreshold ?? null,
                children: node.children.map(serializeLogicNode)
            };
        case 'sequence':
            return {
                ...baseNode,
                kind: 'sequence',
                mode: node.mode,
                stepTiming: Object.fromEntries(
                    Object.entries(node.stepTiming).map(([id, timing]) => [
                        id,
                        {
                            triggerDuration: timing.triggerDuration,
                            within: timing.within
                                ? {
                                      ...timing.within,
                                      minimum: timing.within.minimum ?? 0,
                                      duration: timing.within.duration ?? null
                                  }
                                : null
                        }
                    ])
                ),
                stepCaptures: Object.fromEntries(
                    Object.entries(node.stepCaptures ?? {}).map(([id, capture]) => [
                        id,
                        { key: capture.key, sourceRef: serializeContextFeatureRef(capture.sourceRef) }
                    ])
                ),
                children: node.children.map(serializeLogicNode)
            };
        case 'condition':
            return {
                ...baseNode,
                kind: 'condition',
                templateKey: node.templateKey ?? null,
                requirementType: node.requirementType ?? null,
                featureRef: node.featureRef ? serializeContextFeatureRef(node.featureRef) : null,
                operator: node.operator ?? null,
                value: node.value ?? null,
                parameters: node.parameters ?? null,
                missingDataPolicy: node.missingDataPolicy ?? null
            };
        case 'action':
            return {
                ...baseNode,
                kind: 'action',
                templateKey: node.templateKey ?? null,
                actionKind: node.actionKind,
                parameters: node.parameters
            };
        case 'schedule':
            return {
                ...baseNode,
                kind: 'schedule',
                scheduleKind: node.scheduleKind,
                parameters: normalizeScheduleParametersForBackend(node)
            };
        case 'reference':
            return {
                ...baseNode,
                kind: 'reference',
                referenceKind: node.referenceKind,
                referenceId: node.referenceId,
                version: node.version ?? null,
                parameters: node.parameters ?? null
            };
    }
};

const hydrateLogicNodeFromBackend = (node: SerializedLogicNode): LogicNode => {
    const baseNode = hydrateBaseNodeFromBackend(node);

    switch (node.kind) {
        case 'group':
            return {
                ...baseNode,
                kind: 'group',
                operator: node.operator,
                threshold: node.threshold,
                weightThreshold: node.weightThreshold,
                children: node.children.map(hydrateLogicNodeFromBackend)
            } satisfies LogicGroupNode;

        case 'sequence':
            return {
                ...baseNode,
                kind: 'sequence',
                mode: node.mode,
                stepTiming: node.stepTiming ?? {},
                stepCaptures: Object.fromEntries(
                    Object.entries(node.stepCaptures ?? {}).map(([id, capture]) => [
                        id,
                        { key: capture.key, sourceRef: hydrateContextFeatureRefFromBackend(capture.sourceRef) }
                    ])
                ),
                children: node.children.map(hydrateLogicNodeFromBackend)
            } satisfies LogicSequenceNode;

        case 'condition':
            return {
                ...baseNode,
                kind: 'condition',
                templateKey: node.templateKey,
                requirementType: node.requirementType,
                featureRef: node.featureRef ? hydrateContextFeatureRefFromBackend(node.featureRef) : null,
                operator: node.operator,
                value: node.value,
                parameters: node.parameters ?? undefined,
                missingDataPolicy: node.missingDataPolicy
            } satisfies LogicConditionNode;

        case 'action':
            return {
                ...baseNode,
                kind: 'action',
                templateKey: node.templateKey,
                actionKind: node.actionKind,
                parameters: node.parameters
            } satisfies LogicActionNode;

        case 'schedule':
            return {
                ...baseNode,
                kind: 'schedule',
                scheduleKind: node.scheduleKind,
                parameters: node.parameters
            } satisfies LogicScheduleNode;

        case 'reference':
            return {
                ...baseNode,
                kind: 'reference',
                referenceKind: node.referenceKind,
                referenceId: node.referenceId,
                version: node.version,
                parameters: node.parameters ?? undefined
            } satisfies LogicReferenceNode;

        default:
            return assertNeverSerializedNode(node);
    }
};

const hydrateBaseNodeFromBackend = (node: SerializedLogicNodeBase): LogicNodeBase => ({
    id: node.id,
    label: node.label ?? undefined,
    description: node.description ?? undefined,
    enabled: node.enabled,
    tags: node.tags ?? undefined,
    metadata: node.metadata ?? undefined
});

const hydrateContextFeatureRefFromBackend = (featureRef: SerializedContextFeatureRef): TradeLogicContextFeatureRef => ({
    kind: 'contextFeature',
    featureKey: featureRef.featureKey,
    namespace: featureRef.namespace,
    duration: featureRef.duration,
    bindingKey: featureRef.bindingKey,
    entityType: featureRef.entityType,
    entityKey: featureRef.entityKey,
    selectors: featureRef.selectors,
    filters: featureRef.filters?.map(hydrateFeatureFilterFromBackend) ?? null,
    offsetBars: featureRef.offsetBars,
    lookbackBars: featureRef.lookbackBars,
    trailingMode: featureRef.trailingMode,
    withinBars: featureRef.withinBars,
    missingDataPolicy: featureRef.missingDataPolicy
});

const hydrateFeatureFilterFromBackend = (filter: SerializedFeatureFilter): TradeLogicFeatureFilter => ({
    field: filter.field,
    operator: filter.operator,
    value: filter.value
});

const assertNeverSerializedNode = (node: never): never => {
    throw new Error(`Unsupported serialized logic node kind: ${JSON.stringify(node)}`);
};

const createBaseNodePayload = (node: LogicNode): SerializedLogicNodeBase => ({
    id: node.id,
    label: node.label ?? null,
    description: node.description ?? null,
    enabled: node.enabled,
    tags: node.tags ?? null,
    metadata: node.metadata ?? null
});

const serializeContextFeatureRef = (featureRef: TradeLogicContextFeatureRef): SerializedContextFeatureRef => ({
    kind: featureRef.kind,
    featureKey: featureRef.featureKey,
    namespace: featureRef.namespace ?? null,
    duration: featureRef.duration ?? null,
    bindingKey: featureRef.bindingKey ?? null,
    entityType: featureRef.entityType ?? null,
    entityKey: featureRef.entityKey ?? null,
    selectors: featureRef.selectors ?? null,
    filters: featureRef.filters?.map(serializeFeatureFilter) ?? null,
    offsetBars: featureRef.offsetBars ?? null,
    lookbackBars: featureRef.lookbackBars ?? null,
    trailingMode: featureRef.trailingMode ?? null,
    withinBars: featureRef.withinBars ?? null,
    missingDataPolicy: featureRef.missingDataPolicy ?? null
});

const serializeFeatureFilter = (filter: TradeLogicFeatureFilter): SerializedFeatureFilter => ({
    field: filter.field,
    operator: filter.operator,
    value: filter.value ?? null
});
